import * as GeoJSON from 'geojson'
import { GeoJsonProperties } from 'geojson'
import { extend } from 'lodash-es'
import Supercluster from 'supercluster'

export type Position = {
  latitude: number
  longitude: number
}
export type PointProperties<T extends Position = Position> = { item: T }

export type ClusterConstructorOptions<
  PointData extends Position = Position,
  ClusterOptions extends GeoJsonProperties = Supercluster.AnyProps,
> = {
  map?: google.maps.Map | null | undefined
} & Omit<Supercluster.Options<PointProperties<PointData>, ClusterOptions>, 'map'>

/**
 * @ignore
 */
class OverlayViewSafe {
  constructor() {
    // MarkerClusterer implements google.maps.OverlayView interface. We use the
    // extend function to extend MarkerClusterer with google.maps.OverlayView
    // because it might not always be available when the code is defined so we
    // look for it at the last possible moment. If it doesn't exist now then
    // there is no point going ahead :)
    extend(OverlayViewSafe.prototype, google.maps.OverlayView.prototype)
  }
}

export type ClusterProperties<T extends Position = Position> = {
  cluster?: boolean
  items: Set<T>
}

export class SuperClustererExternalClusterer<
  PointData extends Position = Position,
  ClusterOptions extends ClusterProperties<PointData> = ClusterProperties<PointData>,
> extends (OverlayViewSafe as typeof google.maps.OverlayView) {
  clusterer: Supercluster<PointProperties<PointData>, ClusterOptions>

  constructor({
    radius = 60,
    map = undefined,
    ...rest
  }: ClusterConstructorOptions<PointData, ClusterOptions>) {
    super()
    this.clusterer = new Supercluster({
      ...rest,
      radius,
      map: (i) => {
        return { items: new Set<PointData>([i.item]) } as ClusterOptions
      },
      reduce: (accumulated, current) => {
        accumulated.items = new Set([...accumulated.items, ...current.items])
      },
    })

    if (map) {
      this.setMap(map)
    }
  }

  setMap(map: google.maps.Map | null) {
    super.setMap?.(map)
  }

  getProjection() {
    return super.getProjection?.()
  }

  load(items: PointData[]) {
    this.clusterer.load(
      items.map((item) => ({
        type: 'Feature',
        properties: {
          item,
        },
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
      })),
    )
  }

  getClusters(
    bounds: GeoJSON.BBox,
    zoom: number,
  ): Array<
    | Supercluster.ClusterFeature<ClusterOptions>
    | Supercluster.PointFeature<PointProperties<PointData>>
  > {
    return this.clusterer.getClusters(bounds, zoom)
  }
}
